import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { Wrapper, H, P, Divider } from '@farewill/ui'
import { COLOR, BORDER, FONT, GTR } from '@farewill/ui/tokens'
import { screenMin } from '@farewill/ui/helpers/responsive'

import { formatPriceInPounds } from 'lib/formatting/pricing'
import PreventOrphan from 'components/PreventOrphan'

import { STACK_BREAKPOINT } from './constants'
import DecorativeList from './DecorativeList'

const StyledOption = styled(Wrapper)`
  display: contents; /* This makes the wrapper transparent to the grid */
  box-sizing: border-box;
`

const StyledCard = styled(Wrapper)`
  box-shadow: ${BORDER.SHADOW.M};
  border-radius: ${BORDER.RADIUS.XL};

  ${screenMin[STACK_BREAKPOINT]`
    display: grid;
    grid-template-rows: subgrid;
    grid-row: 1 / -1;
  `}
`

const StyledHeader = styled(Wrapper)`
  grid-row: header;
`

const StyledDescription = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  grid-row: description;
  justify-content: flex-start;
`

const StyledDivider = styled(Divider)`
  grid-row: divider;
`

const StyledBottom = styled.div`
  grid-row: content;
`

const StyledTitle = styled(H)`
  margin-bottom: ${GTR.S};
  padding-right: ${GTR.M};
`

const StyledPriceWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledPrice = styled(P)<{ $priceBottomNote?: boolean }>`
  font-family: ${FONT.FAMILY.DECORATIVE};
  font-size: ${FONT.SIZE.XL};

  span {
    padding-left: ${GTR.XXS};
    font-size: ${FONT.SIZE.M};
    font-family: ${FONT.FAMILY.BODY};
  }

  ${({ $priceBottomNote }) => `
    ${$priceBottomNote ? 'margin-bottom: 0; line-height: 1.2;' : ''}
  `}

  ${screenMin.m`
    font-size: ${FONT.SIZE['3XL']};
  `}

  ${screenMin.l`
    font-size: ${FONT.SIZE['3XL']};
  `}
`

const StyledDescriptionText = styled(P)`
  font-size: ${FONT.SIZE.M};

  ${screenMin.l`
    font-size: ${FONT.SIZE.L};
  `};
`

type OptionProps = {
  className?: string
  title: string
  priceNote: string
  priceBottomNote?: string
  price: number
  description: string
  list: {
    title: string
    items: string[]
  }[]
  children: ReactNode
}

const Option = ({
  className,
  title,
  priceNote,
  priceBottomNote,
  price,
  description,
  children,
  list,
}: OptionProps): React.ReactElement => (
  <StyledOption className={className} tag="article">
    <StyledCard
      background={COLOR.WHITE}
      padding="M"
      paddingFromM="M"
      paddingFromL="L"
      paddingFromXL="XL"
    >
      <StyledHeader noTrim tag="header" margin={[0]}>
        <StyledTitle
          tag="h3"
          size="M"
          margin={[0, 0, 'S']}
          marginFromM={[0, 0, 'L']}
          marginFromL={[0, 'XL', 'L', 0]}
        >
          {title}
        </StyledTitle>

        <Divider margin={['S', 0, 'S']} marginFromM={['S', 0, 'M']} />

        <StyledPriceWrapper margin={[0, 0, 'S']}>
          <Wrapper>
            <P
              margin={[0, 'M', 0, 0]}
              marginFromL={priceBottomNote ? 0 : [0, 0, 'XS']}
              size={priceBottomNote ? 'L' : 'M'}
              strong
            >
              <PreventOrphan>{priceNote}</PreventOrphan>
            </P>
            <StyledPrice $priceBottomNote={!!priceBottomNote} strong>
              {formatPriceInPounds(price)}
            </StyledPrice>
            {priceBottomNote && (
              <P margin={0} size="M" strong>
                <PreventOrphan>{priceBottomNote}</PreventOrphan>
              </P>
            )}
          </Wrapper>
        </StyledPriceWrapper>
      </StyledHeader>
      <StyledDescription marginFromM={[0, 0, 'M']}>
        <StyledDescriptionText margin="0">{description}</StyledDescriptionText>
      </StyledDescription>

      <StyledDivider margin={['M', 0, 'S']} marginFromM={['S', 0, 'M']} />
      <StyledBottom>
        <DecorativeList listItems={list} />
        {children}
      </StyledBottom>
    </StyledCard>
  </StyledOption>
)

export default Option
